export type ServiceType = 'CONSULTATION' | 'EXAM' | 'PROCEDURE' | 'PACKAGE'
//| 'TELECONSULTATION'

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
    PACKAGE: 'Pacote',
    //TELECONSULTATION: 'Teleconsulta',
}

export const SERVICE_TYPE_CATEGORY: Record<ServiceType, string> = {
    CONSULTATION: 'CONSULTATION',
    EXAM: 'EXAM',
    PROCEDURE: 'PROCEDURE',
    PACKAGE: 'PACKAGE',
    //TELECONSULTATION: 'TELECONSULTATION',
}

export interface ExamSelectedItem {
    catalogId: number
    name: string
    catalogTypeName: string
    unitPrice: number
    quantity: number
    maxDiscount?: number
    partnerId?: number
    partnerName?: string
    partners?: { partnerId: number; name: string; price: number }[]
    // Variação escolhida: substitui o preço base do item. Cada variação vira
    // uma linha própria na pilha (mesmo catalogId, catalogVariationId distinto).
    catalogVariationId?: number
    variationName?: string
}

export interface OrderSelectedItem {
    catalogId: number
    name: string
    catalogTypeName: string
    unitPrice: number
    quantity: number
    maxDiscount?: number
    // Variação escolhida: substitui o preço base do item. Cada variação vira
    // uma linha própria na pilha (mesmo catalogId, catalogVariationId distinto).
    catalogVariationId?: number
    variationName?: string
}

export interface ConsultationVariationItem {
    catalogVariationId: number
    quantity: number
    name: string
    unitPrice: number
}

export type OrderExamPriceSource =
    | 'item'
    | 'partner'
    | 'professional'
    | 'custom'

export interface OrderExamPartnerOption {
    partnerId: number
    name: string
    price: number
}

export interface OrderExamProfessionalOption {
    professionalId: number
    name: string
    price: number
}

export interface OrderExamItem {
    catalogId: number
    name: string
    catalogTypeName: string
    unitPrice: number
    itemPrice: number
    quantity: number
    complimentary: boolean
    notes?: string
    priceSource: OrderExamPriceSource
    selectedPartnerId?: number
    selectedProfessionalId?: number
    availablePartners: OrderExamPartnerOption[]
    availableProfessionals: OrderExamProfessionalOption[]
    // Variação escolhida (Catalog filho). Quando presente, itemPrice/availablePartners/
    // availableProfessionals refletem a VARIAÇÃO, não o item pai.
    catalogVariationId?: number
    variationName?: string
    /**
     * Quem RECEBE por este componente — separado da "fonte do valor", que é quanto se
     * COBRA. Um parceiro pode dar o preço sem ser quem executa, e a consulta
     * pré-anestésica costuma ser do hospital, que não aparece em fonte de valor nenhuma.
     *
     * Vazio: sem repasse próprio (comportamento antigo).
     */
    recipientKind?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | null
    recipientId?: number | null
    /**
     * Quanto o destinatário recebe, em centavos. Em `PARTNER`, vazio cai no custo do
     * vínculo (`CatalogPartner.price`), como já acontece no exame. Em `PROFESSIONAL` e
     * `HOSPITAL` não há de onde herdar: é digitado.
     */
    recipientAmount?: number | null
    /** Nome do destinatário, só para exibir ao reabrir. */
    recipientName?: string | null
    /**
     * Quando ESTE componente acontece — venda de pacote.
     *
     * O procedimento num dia, a consulta de retorno em outro. Só aparece na edição de
     * venda de pacote: no agendamento comum a data é do atendimento inteiro.
     */
    scheduledAt?: string | null
    arrivalOrder?: boolean
    /**
     * Sala deste componente — venda de pacote.
     *
     * Anda junto com a data: se cada parte acontece num dia, cada uma pode acontecer
     * numa sala. A do atendimento continua valendo para o procedimento em si.
     */
    roomId?: number | null
    /**
     * O repasse foi editado à mão.
     *
     * Sem a marca, o valor do repasse ACOMPANHA o preço unitário — que é o caso normal:
     * o componente é executado por terceiro e o valor cheio vai para ele. Com a marca, o
     * valor digitado manda, e mudar o preço não o sobrescreve.
     */
    recipientAmountEdited?: boolean
}

export interface MedicalSupplyItem {
    medicalSupplyId: number
    name: string
    type: string
    quantity: number
    /** Valor COBRADO do paciente. */
    unitPrice: number
    /**
     * Custo unitário PAGO a quem forneceu — é o que vira repasse. Obrigatório: sem ele o
     * repasse sai zero, que foi o que aconteceu com 25.572 linhas de OPME até 2026-07-30.
     */
    costPriceSnapshot?: number
    /**
     * Quem recebe: `SUPPLIER` (padrão, o fornecedor do cadastro ou outro escolhido na hora),
     * `HOSPITAL` quando o material sai do próprio hospital, ou `PROFESSIONAL`/`PARTNER`
     * quando é quem o adquiriu que cobra. Fica na linha porque o fornecedor do OPME muda a
     * cada orçamento.
     */
    recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER'
    recipientId?: number | null
    /** Nome do destinatário escolhido, só para exibir. */
    recipientName?: string | null
    /**
     * Fornecedor do CADASTRO do material, se houver. Não é escolha do operador: serve
     * para a tela saber se, deixando o campo em branco, existe de quem herdar. Sem
     * fornecedor na linha e sem fornecedor no cadastro, o custo é cobrado e a conta a
     * pagar nunca nasce — por isso vira bloqueio.
     */
    defaultSupplierId?: number | null
}
